import {
  ListItem,
  ListItemAvatar,
  ListItemButton,
  type ListItemButtonProps,
  type ListItemProps,
  ListItemText,
  TypographyProps,
} from "@mui/material";
import React from "react";
import { Stack } from "./layout/Stack.js";

interface ProfileListItemProps {
  size?: "m" | "s";
  avatar?: JSX.Element;
  badge?: JSX.Element;
  category?: JSX.Element;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  isNotActive?: boolean;
  RightButton?: JSX.Element;
  ActionButton?: JSX.Element;
  ActionButtons?: JSX.Element[];
  primaryTypographyProps?: TypographyProps;
  secondaryTypographyProps?: TypographyProps;
  className?: string;
}

export function ProfileListItem({
  size = "m",
  avatar,
  badge,
  category,
  isNotActive = false,
  primary,
  secondary,
  primaryTypographyProps,
  secondaryTypographyProps,
  RightButton,
  ActionButton,
  ActionButtons,
  className,
  listItemProps,
}: ProfileListItemProps & {
  listItemProps?: ListItemProps;
}) {
  const hasInfo = !!(badge || category || RightButton);
  const hasAction = !!(ActionButton || ActionButtons?.length);
  const actionCount = hasAction ? (ActionButtons?.length ?? 1) : 0;
  const actionsPadding = 56 + (actionCount - 1) * 48;

  return (
    <ListItem
      sx={{
        "&.MuiListItem-secondaryAction": {
          paddingRight: `${actionsPadding}px`,
        },
      }}
      classes={{ container: className }}
      secondaryAction={
        hasAction ? (
          <Stack spacing={1 / 2} alignItems="center">
            {ActionButton}
            {ActionButtons?.length ? ActionButtons : null}
          </Stack>
        ) : null
      }
      {...listItemProps}
    >
      {avatar ? (
        <ListItemAvatar
          sx={
            size === "m"
              ? theme => ({ minWidth: theme.layout.listItemMinWidth.lg })
              : size === "s"
                ? theme => ({ minWidth: theme.layout.listItemMinWidth.sm })
                : undefined
          }
        >
          {avatar}
        </ListItemAvatar>
      ) : null}
      <ListItemText
        style={isNotActive ? { opacity: 0.4 } : undefined}
        primary={primary}
        secondary={secondary}
        slotProps={{
          primary: {
            ...(primaryTypographyProps ?? {}),
            style: {
              display: "-webkit-box",
              overflow: "hidden",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              maxHeight: 48,
              ...(primaryTypographyProps?.style ?? {}),
            },
          },

          secondary: {
            ...(secondaryTypographyProps ?? {}),
            style: {
              display: "-webkit-box",
              overflow: "hidden",
              //WebkitLineClamp: size === "m" && number ? 3 : 2,
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              maxHeight: 40,
              ...(secondaryTypographyProps?.style ?? {}),
            },
          },
        }}
      />
      {hasInfo && (
        <Stack
          spacing={1 / 2}
          alignItems="center"
          style={{ pointerEvents: "none", marginLeft: "8px" }}
        >
          {badge ? (
            <span style={isNotActive ? { opacity: 0.6 } : undefined}>
              {badge}
            </span>
          ) : null}
          {category ? (
            <span style={isNotActive ? { opacity: 0.6 } : undefined}>
              {category}
            </span>
          ) : null}
          {RightButton ? (
            <span
              style={{ pointerEvents: "all" }}
              onMouseDown={e => {
                e.stopPropagation();
              }}
              onTouchStart={e => {
                e.stopPropagation();
              }}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {RightButton}
            </span>
          ) : null}
        </Stack>
      )}
    </ListItem>
  );
}

type ProfileListItemButtonProps = {
  size?: "m" | "s";
  avatar?: JSX.Element;
  badge?: JSX.Element;
  category?: JSX.Element;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  isNotActive?: boolean;
  RightButton?: JSX.Element;
  ActionButton?: JSX.Element;
  ActionButtons?: JSX.Element[];
  primaryTypographyProps?: TypographyProps;
  secondaryTypographyProps?: TypographyProps;
  className?: string;
};

export function ProfileListItemButton<Component extends React.ElementType>({
  size = "m",
  avatar,
  badge,
  category,
  isNotActive = false,
  primary,
  primaryTypographyProps,
  secondary,
  secondaryTypographyProps,
  RightButton,
  ActionButton,
  ActionButtons,
  className,
  listItemProps,
  onClick,
}: ProfileListItemButtonProps & {
  listItemProps?: { component?: Component } & ListItemButtonProps<Component>;
  onClick?: ListItemButtonProps<Component>["onClick"];
}) {
  const hasInfo = !!(badge || category || RightButton);
  const hasAction = !!(ActionButton || ActionButtons?.length);
  const actionCount = hasAction ? (ActionButtons?.length ?? 1) : 0;
  const actionsPadding = 56 + (actionCount - 1) * 48;

  return (
    <ListItem
      disablePadding
      secondaryAction={
        hasAction ? (
          <Stack spacing={1 / 2} alignItems="center">
            {ActionButton}
            {ActionButtons?.length ? ActionButtons : null}
          </Stack>
        ) : null
      }
    >
      <ListItemButton
        classes={{ root: className }}
        {...listItemProps}
        onClick={(...args) => {
          onClick?.(...args);
          listItemProps?.onClick?.(...args);
        }}
        sx={
          hasAction
            ? {
                "&.MuiListItemButton-gutters": {
                  paddingRight: `${actionsPadding}px`,
                },
              }
            : undefined
        }
      >
        {avatar ? (
          <ListItemAvatar
            sx={
              size === "m"
                ? theme => ({ minWidth: theme.layout.listItemMinWidth.lg })
                : size === "s"
                  ? theme => ({ minWidth: theme.layout.listItemMinWidth.sm })
                  : undefined
            }
          >
            {avatar}
          </ListItemAvatar>
        ) : null}
        <ListItemText
          style={isNotActive ? { opacity: 0.4 } : undefined}
          primary={primary}
          secondary={secondary}
          slotProps={{
            primary: {
              ...(primaryTypographyProps ?? {}),
              style: {
                display: "-webkit-box",
                overflow: "hidden",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                maxHeight: 48,
                ...(primaryTypographyProps?.style ?? {}),
              },
            },

            secondary: {
              ...(secondaryTypographyProps ?? {}),
              style: {
                display: "-webkit-box",
                overflow: "hidden",
                //WebkitLineClamp: size === "m" && number ? 3 : 2,
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                maxHeight: 40,
                ...(secondaryTypographyProps?.style ?? {}),
              },
            },
          }}
        />
        {hasInfo && (
          <Stack
            spacing={1 / 2}
            alignItems="center"
            style={{ pointerEvents: "none", marginLeft: "8px" }}
          >
            {badge ? (
              <span style={isNotActive ? { opacity: 0.6 } : undefined}>
                {badge}
              </span>
            ) : null}
            {category ? (
              <span style={isNotActive ? { opacity: 0.6 } : undefined}>
                {category}
              </span>
            ) : null}
            {RightButton ? (
              <span
                style={{ pointerEvents: "all" }}
                onMouseDown={e => {
                  e.stopPropagation();
                }}
                onTouchStart={e => {
                  e.stopPropagation();
                }}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {RightButton}
              </span>
            ) : null}
          </Stack>
        )}
      </ListItemButton>
    </ListItem>
  );
}
